/* NOCTURA motion layer — v2
   One self-contained file: injects its own CSS, no libraries, no CDN.
   Progressive enhancement — nothing is hidden unless this script runs.
   Reduced-motion visitors and the Shopify theme editor get static pages.

   NOTE ON SPECIFICITY: theme.liquid carries a legacy "motion & life" <style>
   block in the BODY, so it outranks anything injected into <head> on a tie.
   Doubled selectors (.card.card) are deliberate — they win that tie. If that
   legacy block is ever deleted, these rules still behave identically. */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITOR = !!(window.Shopify && Shopify.designMode);
  var EASE = 'cubic-bezier(.22,1,.36,1)'; /* house ease — same curve as .reveal in noctura.css */
  var TILT = 'rotate .4s ' + EASE;

  var css =
  /* ---- always on: hard corners, and a reduced-motion guard for the legacy
          page-entrance animation, which ships without one ---- */
  '.card.card,.card.card img,.card__frame,.card__frame img,' +
  '.lx-paper,.lx-paper img,.lx-mount,.frame,.frame>img{border-radius:0}' +
  '.card.card{overflow:visible}' + /* 30px radius clipped the frame corners */
  '@media (prefers-reduced-motion: reduce){' +
    'main,#MainContent,.main-content{animation:none}' +
    '.card__frame,.lx-mount{rotate:0deg}' +
  '}' +

  '@media (prefers-reduced-motion: no-preference){' +
    'html{scroll-behavior:smooth;scroll-padding-top:calc(var(--header-h,66px) + 12px)}' +

    /* entrance states — classes only ever added by this script */
    '.nmh{opacity:0;transform:translateY(12px)}' +
    '.nmv{opacity:1;transform:none;transition:opacity .65s ' + EASE + ',transform .65s ' + EASE + '}' +

    /* lazy-image fade-in */
    'img.nmi{opacity:0}' +
    'img.nmi-in{opacity:1;transition:opacity .5s ease-out}' +

    /* ---- angled artwork: every print rests off-square and straightens under
            the cursor. `rotate` is its own property, so it composes with the
            entrance/hover transforms instead of overwriting them. ---- */
    '.lx-mount{transition:' + TILT + '}' +
    /* redeclares the theme's .card__frame transition with rotate appended */
    '.card__frame{transition:transform .42s ' + EASE + ',box-shadow .42s ' + EASE + ',border-color .42s ease,' + TILT + '}' +
    '.lx-stack figure:first-child .lx-mount{rotate:-2.5deg}' +
    '.lx-stack figure:last-child .lx-mount{rotate:2deg}' +
    '.lx-grid > .lx-card:nth-child(3n+1) .lx-mount{rotate:-1.6deg}' +
    '.lx-grid > .lx-card:nth-child(3n+2) .lx-mount{rotate:1.2deg}' +
    '.lx-grid > .lx-card:nth-child(3n+3) .lx-mount{rotate:-.7deg}' +
    '.grid > .card:nth-child(3n+1) .card__frame{rotate:-1.4deg}' +
    '.grid > .card:nth-child(3n+2) .card__frame{rotate:1.1deg}' +
    '.grid > .card:nth-child(3n+3) .card__frame{rotate:-.6deg}' +
    '.lx-card:hover .lx-mount,.lx-card:focus-visible .lx-mount,' +
    '.card:hover .card__frame,.card:focus-visible .card__frame{rotate:0deg}' +

    /* ---- card hover: one gesture only. The legacy block lifted the whole
            card AND zoomed the image 5%; both are cancelled here so the
            theme's own frame lift reads as a single quiet movement. ---- */
    '.card.card:hover,.card.card:focus-visible{transform:none}' +
    '.card.card:hover img,.card.card:focus-visible img{transform:none}' +

    /* button micro-interactions — motion only, theme colours untouched */
    '.btn,.field button{transition:background .24s ease,color .24s ease,border-color .24s ease,transform .18s ' + EASE + '}' +
    '.btn:hover,.field button:hover{transform:translateY(-1px)}' +
    '.btn:active,.field button:active{transform:translateY(0)}' +
    '.lx-btn.lx-btn{transition:opacity .18s ease,transform .18s ' + EASE + ',box-shadow .18s ' + EASE + '}' +
    '.lx-btn--ghost:hover{transform:translateY(-1px)}' +
    '.lx-btn--solid:hover{transform:translate(2px,2px);box-shadow:4px 4px 0 var(--yellow)}' +
    '.lx-btn--solid:active{transform:translate(5px,5px);box-shadow:1px 1px 0 var(--yellow)}' +

    /* LOUD card hover: paper eases off its ink block — completes the existing
       .lx-mount::before 7px→12px offset shift from the section styles */
    '.lx-paper{transition:transform .24s cubic-bezier(.2,.9,.25,1)}' +
    '.lx-card:hover .lx-paper,.lx-card:focus-visible .lx-paper{transform:translate(-2px,-2px)}' +
  '}';
  var tag = document.createElement('style');
  tag.textContent = css;
  document.head.appendChild(tag);

  /* Reduced motion stops here: the tilts, hard corners and hover/press styles
     above still apply, but nothing is ever hidden or observed. The theme
     editor gets the motion too (Gregory wants to see it there); sections it
     re-renders are shown at once, below. */
  if (REDUCE) return;

  /* ---------- lazy-image fade-in ---------- */
  document.querySelectorAll('main img[loading="lazy"]').forEach(function (img) {
    if (img.complete) return; /* cached — never blank an image that's already there */
    /* Nocturne components run their own opacity (hover layers, gallery views) */
    if (img.closest('.noc-card,.noc-spine,.noc-gallery,.noc-scale,.noc-tile,.noc-pair,.noc-hero')) return;
    img.classList.add('nmi');
    var show = function () { img.classList.add('nmi-in'); };
    img.addEventListener('load', show, { once: true });
    img.addEventListener('error', show, { once: true });
    if (img.complete) show(); /* loaded between the check and the listener */
  });
  /* rescue any image whose load event never fires */
  setTimeout(function () {
    document.querySelectorAll('img.nmi:not(.nmi-in)').forEach(function (img) {
      img.classList.add('nmi-in');
    });
  }, 4000);

  if (!('IntersectionObserver' in window)) return;

  /* ---------- Shop v2 fade-up: print grids ----------
     Items marked [data-rise] start at opacity 0 / +48px and rise over
     0.8–0.9s when 20% of them is in view, staggered 90ms by column. Items
     already on screen when this runs are shown at once, so the first row
     never blinks out and back. Re-sorted grids mark their items .is-in. */
  var riseIO = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      var el = en.target, row = el.parentNode;
      var lefts = [];
      Array.prototype.forEach.call(row ? row.children : [], function (c) {
        var l = Math.round(c.offsetLeft);
        if (lefts.indexOf(l) === -1) lefts.push(l);
      });
      lefts.sort(function (x, y) { return x - y; });
      var col = Math.max(0, lefts.indexOf(Math.round(el.offsetLeft)));
      el.style.setProperty('--rise-delay', (col * 90) + 'ms');
      el.classList.add('is-in');
      riseIO.unobserve(el);
    });
  }, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' });
  var vh = window.innerHeight;
  document.querySelectorAll('[data-rise]').forEach(function (el) {
    var r = el.getBoundingClientRect();
    if (r.top < vh && r.bottom > 0) { el.classList.add('is-in'); return; }
    riseIO.observe(el);
  });
  document.documentElement.classList.add('noc-rise-on');
  /* theme editor: a re-rendered section brings fresh [data-rise] items that
     nothing observes, and noc-rise-on would keep them hidden; show them */
  if (EDITOR) {
    document.addEventListener('shopify:section:load', function (e) {
      e.target.querySelectorAll('[data-rise]').forEach(function (el) { el.classList.add('is-in'); });
    });
  }

  function settle(el) {
    el.addEventListener('transitionend', function () {
      el.classList.remove('nmh');
      el.style.transitionDelay = '';
    }, { once: true });
  }

  /* ---------- hero choreography (LOUD homepage, on page load) ---------- */
  var hero = document.querySelector('.lx-hero');
  if (hero) {
    var steps = ['.lx-label', '.lx-hero__ttl', '.lx-hero__body', '.lx-cta'];
    var cast = [];
    steps.forEach(function (sel, i) {
      var el = hero.querySelector(sel);
      if (el) cast.push([el, i * 90]);
    });
    hero.querySelectorAll('.lx-stack figure').forEach(function (el, i) {
      cast.push([el, 140 + i * 130]);
    });
    cast.forEach(function (p) { p[0].classList.add('nmh'); });
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      cast.forEach(function (p) {
        p[0].style.transitionDelay = p[1] + 'ms';
        p[0].classList.add('nmv');
        settle(p[0]);
      });
    }); });
  }

  /* ---------- scroll reveals ---------- */
  var targets = [];
  function collect(sel) {
    document.querySelectorAll(sel).forEach(function (el) {
      if (el.classList.contains('nmh') || el.classList.contains('nmv')) return;
      if (el.classList.contains('reveal')) return; /* noctura.js owns .reveal */
      if (el.closest('#shopify-section-cart-drawer,.drawer')) return;
      if (el.parentNode && el.parentNode.closest('.nmh')) return; /* ancestor already animates */
      el.classList.add('nmh');
      targets.push(el);
    });
  }
  /* LOUD homepage inner units — the whole page is one section, so reveal its parts */
  collect('.lx-tick');
  collect('.lx-band__head');
  collect('.lx-grid > *');
  collect('.lx-room');
  collect('.lx-specs > *');
  collect('.lx-signup__grid > *');
  /* shared template furniture */
  collect('main .collection__head');
  collect('main .page__title');
  collect('main .pagination');
  /* fallback: sections that have no inner targets and no .reveal system of
     their own fade in as one block (cart, pages, blog). The product page's
     main section is exempt: hiding it hid the image, price and Add to cart
     until this deferred script ran and the fade finished. Its body class is
     template-product for every product template (template.name ignores the
     per-print suffix); the section id ends in __main. Wall shots etc. below
     it still fade in. */
  var PRODUCT = document.body.classList.contains('template-product');
  document.querySelectorAll('main > .shopify-section').forEach(function (sec) {
    if (sec.id === 'shopify-section-cart-drawer') return;
    if (PRODUCT && sec.id.indexOf('__main') !== -1) return;
    if (sec.querySelector('.nmh,.reveal,[data-rise],.noc-hero')) return;
    sec.classList.add('nmh');
    targets.push(sec);
  });

  var io = new IntersectionObserver(function (entries) {
    var order = new Map(); /* stagger siblings that enter together */
    entries.forEach(function (en) {
      if (!en.isIntersecting) return;
      var el = en.target;
      var n = order.get(el.parentNode) || 0;
      order.set(el.parentNode, n + 1);
      el.style.transitionDelay = Math.min(n * 70, 420) + 'ms';
      el.classList.add('nmv');
      settle(el);
      io.unobserve(el);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  targets.forEach(function (el) { io.observe(el); });
})();
